<template>
  <div class="rire">
    <!-- Hero -->
    <section class="rire__hero container">
      <p class="eyebrow reveal">Notre parti pris</p>
      <h1 class="rire__title reveal" style="animation-delay:.05s">
        Peut-on rire<br><span class="hl">de tout&nbsp;?</span>
      </h1>
      <p class="rire__lead reveal" style="animation-delay:.12s">
        Oui. Probablement. Mais pas forcément avec tout le monde, et pas
        n'importe comment.
      </p>
    </section>

    <!-- Manifeste -->
    <section class="container manifesto">
      <p class="manifesto__intro reveal">
        Cette page, c'est pas un avertissement légal, c'est pas nous qui te
        faisons la morale. C'est juste deux-trois trucs qu'on avait envie de se
        dire entre gens qui aiment rire.
      </p>

      <article class="block reveal">
        <h2>Une blague, c'est pas une opinion.</h2>
        <p>
          Ici, t'as des milliers de blagues. Des fines, des lourdes, des
          débiles, et franchement quelques-unes qui ont pris un coup de vieux.
          Des blagues de blondes, de belges, de curés, de tout. On les garde,
          parce que c'est un morceau d'humour populaire et qu'on n'est pas là
          pour repeindre le passé. Rire d'un stéréotype, ça n'a jamais
          transformé personne en salaud. <strong>Le problème n'est jamais la
          blague toute seule.</strong>
        </p>
      </article>

      <article class="block reveal">
        <h2>Le truc, c'est quand ça déteint.</h2>
        <p>
          Un stéréotype, c'est un raccourci. En blague, le raccourci est marrant
          justement parce que tout le monde sait que c'est faux. Le jour où le
          raccourci arrête d'être une vanne et commence à ressembler à
          <em>une vraie façon de voir les gens</em> — là, c'est plus la même
          chose. Rire d'une idée, c'est une chose. Finir par y croire, c'en est
          une autre. Toute la différence est là, et elle tient dans les têtes,
          pas dans la blague.
        </p>
      </article>

      <article class="block reveal">
        <h2>Le vrai skill, c'est de connaître ton public.</h2>
        <p>
          Une blague ne vit pas toute seule&nbsp;: elle vit <em>dans une pièce,
          devant des gens précis</em>. Et là, deux questions valent mieux que
          toutes les règles du monde.
        </p>
        <p>
          D'abord&nbsp;: <strong>qu'est-ce que tu sais vraiment d'eux&nbsp;?</strong>
          Entre trois potes de toujours, tu sais qui encaisse quoi, tu peux y
          aller. Mais plus le cercle s'élargit — collègues, soirée, inconnus —
          plus tu navigues à l'aveugle. Tu ne connais pas l'histoire de chacun,
          ce qui a touché qui. Et le fait que personne ne bronche ne veut
          <em>rien</em> dire&nbsp;: y a des gens qui rient jaune, qui encaissent
          en silence pour ne pas plomber l'ambiance. Le silence, c'est pas un
          oui. «&nbsp;Tout le monde a ri&nbsp;» n'a jamais prouvé que t'avais
          visé juste.
        </p>
        <p>
          Ensuite&nbsp;: <strong>ta blague peut faire deux dégâts opposés en
          même temps.</strong> Elle peut blesser la personne visée — ou
          quelqu'un qui s'y reconnaît sans que tu le saches. Mais elle peut aussi
          <em>conforter</em> le gars d'en face qui, lui, ne rit pas au second
          degré&nbsp;: celui pour qui ton cliché n'est pas une vanne mais une
          confirmation que «&nbsp;c'est normal de penser ça&nbsp;». Ton rire
          complice devient sa validation. La même phrase, deux personnes dans la
          salle, deux manières de faire mal — et le plus souvent, tu ne verras ni
          l'une ni l'autre.
        </p>
      </article>

      <article class="block reveal">
        <h2>Et là, t'entres en jeu.</h2>
        <p>
          Parce que ces blagues, tu vas les ressortir. À l'apéro, au boulot, à
          table. À ce moment-là, c'est plus le site qui parle, c'est toi.
          <strong>T'es le maillon.</strong> Si ça reste une connerie assumée et
          que tout le monde le prend comme ça, nickel, c'est fait pour ça. Mais
          si tu sens que quelqu'un prend le cliché au premier degré et commence à
          s'enfermer dedans pour de vrai — le bon réflexe, c'est pas d'en
          rajouter. C'est plutôt de l'aider à en sortir. Sans en faire un drame.
          Juste, ne pas être celui qui enfonce.
        </p>
      </article>

      <article class="block reveal">
        <h2>Et si une blague coince, tu peux souvent la sauver.</h2>
        <p>
          Jeter une bonne vanne parce qu'un détail dérange, c'est du gâchis. La
          plupart du temps, le ressort marrant ne tient PAS à la cible réelle —
          tu peux la raconter autrement et elle reste drôle&nbsp;:
        </p>
        <ul class="tips">
          <li>
            <strong>Change la cible&nbsp;:</strong> vise un peuple inventé, une
            profession absurde, un objet, toi-même, plutôt qu'un vrai groupe.
            (Une blague de «&nbsp;blonde&nbsp;» marche souvent pareil sur un
            perso fictif tête-en-l'air.)
          </li>
          <li>
            <strong>Retourne-la&nbsp;:</strong> que la chute tombe sur celui qui
            balance le cliché, pas sur sa cible.
          </li>
          <li>
            <strong>Soigne les mots&nbsp;:</strong> le terme cru ou l'insulte
            gratuite n'ajoutent presque jamais au gag — ils le salissent. Un mot
            plus fin est souvent plus drôle qu'un mot vulgaire.
          </li>
        </ul>
        <p>
          C'est pas une règle. C'est juste que les meilleures blagues, ici, sont
          rarement les plus méchantes. Elles sont les plus malignes.
        </p>
      </article>

      <div class="creed reveal">
        <p class="creed__punch">Rire de tout, oui. En rester maître, c'est mieux.</p>
        <p class="creed__soft">On te fait confiance. Marre-toi.</p>
        <p class="creed__sign">— L'équipe de La Banane</p>
      </div>
    </section>

    <!-- Boîte à outils — masquée pour le moment (réactiver en retirant v-if="false") -->
    <section v-if="false" class="container toolbox">
      <div class="toolbox__head reveal">
        <p class="eyebrow">Section pratique</p>
        <h2 class="toolbox__title">La boîte à outils du contournement</h2>
        <p class="toolbox__intro">
          Six techniques pour garder la vanne et déplacer la baffe. Pas pour être
          gentil — pour être plus malin. Une blague qui tape sur un vrai groupe,
          c'est souvent la version la plus paresseuse du gag&nbsp;: le premier
          truc qui passait. Le ressort comique, la plupart du temps, se fout de
          qui est vraiment visé — c'est toi qui décides où tombe le coup.
          Détourne-le, et neuf fois sur dix tu tombes sur une chute meilleure.
        </p>
        <div class="toolbox__reflex">
          <span class="toolbox__reflex-tag">Le réflexe, avant tout</span>
          <p>
            demande-toi <em>«&nbsp;qu'est-ce qui fait rire ici — le mécanisme, ou
            la cible&nbsp;?&nbsp;»</em> Si tu peux remplacer le groupe par un pays
            inventé, un saxophoniste ou ton propre voisin et que ça fait toujours
            rire, c'était le mécanisme depuis le début. La cible réelle ne servait
            à rien. Débranche-la, garde le gag.
          </p>
        </div>
      </div>

      <!-- 1 -->
      <article class="tool reveal">
        <div class="tool__num">1</div>
        <div class="tool__body">
          <h3>La cible fictive — invente le peuple.</h3>
          <p>
            Transfère le cliché sur une nation, un village ou une tribu
            100&nbsp;% inventés. Le cerveau accepte le cliché comme convention de
            jeu, personne de réel n'encaisse — et le gars du fond qui cherchait
            une confirmation de ses idées à la con reste le bec dans l'eau&nbsp;:
            y a pas de vrai groupe à confirmer. Bonus&nbsp;: tu peux charger
            autant que tu veux.
          </p>
          <div class="swap">
            <div class="swap__before">
              <span class="swap__label">Avant</span>
              <p>«&nbsp;Pourquoi les [nationalité] mettent une échelle à la fenêtre pour Noël&nbsp;? Parce que les prix montent.&nbsp;»</p>
            </div>
            <div class="swap__arrow" aria-hidden="true">→</div>
            <div class="swap__after">
              <span class="swap__label">Après</span>
              <p>«&nbsp;À Grumelot-les-Bains, ils mettent une échelle à la fenêtre pour Noël. Pourquoi&nbsp;? Parce que là-bas, les prix montent.&nbsp;»</p>
            </div>
          </div>
          <p class="tool__note">
            Le jeu de mots est <em>tout</em> le gag — il ne dépend pas d'un atome
            du groupe visé. Grumelot fait le job, et Grumelot ne s'est jamais
            plaint.
          </p>
        </div>
      </article>

      <!-- 2 -->
      <article class="tool reveal">
        <div class="tool__num">2</div>
        <div class="tool__body">
          <h3>Le boomerang — la chute change de camp.</h3>
          <p>
            Garde le cliché dans la bouche du perso, mais <strong>redirige la
            chute</strong>&nbsp;: le dindon de la farce, c'est plus la cible,
            c'est le type sûr de lui qui balance le cliché. Deux fois plus drôle,
            parce que personne ne s'attend à ce que la vanne se retourne dans la
            main de celui qui la tient — et tout le monde adore voir tomber le
            donneur de leçon.
          </p>
          <div class="swap">
            <div class="swap__before">
              <span class="swap__label">Avant</span>
              <p>«&nbsp;Les femmes savent pas conduire.&nbsp;»</p>
            </div>
            <div class="swap__arrow" aria-hidden="true">→</div>
            <div class="swap__after">
              <span class="swap__label">Après</span>
              <p>«&nbsp;Mon oncle répète que les femmes savent pas conduire. Il l'a dit trois fois cette année — à chaque fois depuis le siège passager, pendant qu'elle le ramenait du garage.&nbsp;»</p>
            </div>
          </div>
        </div>
      </article>

      <!-- 3 -->
      <article class="tool reveal">
        <div class="tool__num">3</div>
        <div class="tool__body">
          <h3>L'absurdisation — pousse le curseur jusqu'au non-sens.</h3>
          <p>
            Ne retire pas le stéréotype&nbsp;: gonfle-le tellement qu'il décolle
            du réel et devient sa propre parodie. Quand le trait dépasse toute
            réalité possible, plus personne ne le prend au premier degré — on rit
            de l'énormité, pas du groupe. C'est le principe des vannes de
            Toto&nbsp;: ça vise l'idée, pas les gens.
          </p>
          <div class="swap">
            <div class="swap__before">
              <span class="swap__label">Avant</span>
              <p>«&nbsp;Les [groupe], c'est des radins.&nbsp;»</p>
            </div>
            <div class="swap__arrow" aria-hidden="true">→</div>
            <div class="swap__after">
              <span class="swap__label">Après</span>
              <p>«&nbsp;Il est tellement radin qu'il a demandé à être enterré debout, pour pas payer le supplément couchette.&nbsp;»</p>
            </div>
          </div>
          <p class="tool__note">
            Le "il" flou plus l'exagération cosmique&nbsp;: "radin" est devenu un
            personnage de cartoon. Aucun groupe réel là-dedans.
          </p>
        </div>
      </article>

      <!-- 4 -->
      <article class="tool reveal">
        <div class="tool__num">4</div>
        <div class="tool__body">
          <h3>Le transfert de trait — garde le trait, change le porteur.</h3>
          <p>
            Le trait moqué (radin, lent, naïf, vantard) n'appartient à
            personne&nbsp;: c'est un porte-manteau. Décroche-le du groupe et
            raccroche-le sur une profession, une manie, un type de client —
            n'importe quoi qui <strong>se choisit</strong> plutôt que se naît.
            Souvent la nouvelle cible est <em>plus</em> juste que l'ancienne.
          </p>
          <div class="swap">
            <div class="swap__before">
              <span class="swap__label">Avant</span>
              <p>une blague sur «&nbsp;les [religion] et l'argent&nbsp;».</p>
            </div>
            <div class="swap__arrow" aria-hidden="true">→</div>
            <div class="swap__after">
              <span class="swap__label">Après</span>
              <p>la même, sur le trésorier de l'asso qui plastifie ses tickets de caisse et facture le café de la réunion.</p>
            </div>
          </div>
          <p class="tool__note">
            On rit exactement du même travers. Sauf qu'un trésorier radin, ça se
            choisit&nbsp;; une origine, non.
          </p>
        </div>
      </article>

      <!-- 5 -->
      <article class="tool reveal">
        <div class="tool__num">5</div>
        <div class="tool__body">
          <h3>L'auto-dérision blindée — tire d'abord sur ton propre camp.</h3>
          <p>
            La vanne la plus tranquille, c'est celle où tu paies de ta
            personne&nbsp;: ton village, ton métier, ta bande, ta calvitie.
            Personne peut t'accuser de taper sur plus faible quand la première
            victime, c'est toi — et l'aveu sincère est presque toujours plus
            drôle que la moquerie d'en face, parce qu'il a un truc que la vanne
            gratuite n'aura jamais&nbsp;: c'est vrai.
          </p>
          <div class="swap">
            <div class="swap__before">
              <span class="swap__label">Avant</span>
              <p>«&nbsp;Les [région] sont des gros bouffeurs de beurre.&nbsp;»</p>
            </div>
            <div class="swap__arrow" aria-hidden="true">→</div>
            <div class="swap__after">
              <span class="swap__label">Après</span>
              <p>«&nbsp;Nous les Bretons, on met du beurre dans tout. Dans le café, si on nous laisse faire. Le médecin a arrêté de compter.&nbsp;»</p>
            </div>
          </div>
        </div>
      </article>

      <!-- 6 -->
      <article class="tool reveal">
        <div class="tool__num">6</div>
        <div class="tool__body">
          <h3>L'individu (très) précis — un perso, pas une catégorie.</h3>
          <p>
            Au lieu de «&nbsp;les [groupe] sont X&nbsp;», invente UN type, nommé,
            daté, situé. Plus c'est précis, plus c'est drôle&nbsp;: le flou tape
            sur tout le monde, le précis tape sur Gérald. Un couillon nommé Gérald
            n'a jamais insulté une population.
          </p>
          <div class="swap">
            <div class="swap__before">
              <span class="swap__label">Avant</span>
              <p>«&nbsp;Les [nationalité] sont pas malins.&nbsp;»</p>
            </div>
            <div class="swap__arrow" aria-hidden="true">→</div>
            <div class="swap__after">
              <span class="swap__label">Après</span>
              <p>«&nbsp;Gérald Pommier, 54 ans, Vesoul, a passé son permis huit fois. La huitième, il a percuté l'examinateur qui rentrait chez lui à pied.&nbsp;»</p>
            </div>
          </div>
          <p class="tool__note">
            Gérald est bête. Gérald n'est pas un peuple. Et le premier degré du
            fond de la salle n'a rien à se mettre sous la dent&nbsp;: y a pas de
            «&nbsp;eux&nbsp;», y a juste Gérald.
          </p>
        </div>
      </article>

      <div class="outro reveal">
        <p>
          Contourner, c'est pas se brider — c'est viser mieux. Une vanne qui fait
          rire toute la pièce plutôt que la moitié, qui ne file pas de billes au
          premier con venu, et qui reste drôle dans dix ans&nbsp;: c'est pas plus
          sage, c'est plus fort. Le cliché brut, c'est le brouillon de la
          blague&nbsp;; le contournement, c'est la version au propre. Les
          meilleures blagues d'ici sont rarement les plus méchantes — elles sont
          les plus malignes.
        </p>
        <p>
          Et si vraiment enlever la cible tue la blague… c'est peut-être qu'elle
          ne cherchait pas à faire rire, juste à taper. Là, c'est plus une
          question de technique.
        </p>
      </div>
    </section>

    <section class="container rire__cta">
      <BananaButton to="/" size="lg" variant="dark">🍌 Retour aux blagues</BananaButton>
    </section>
  </div>
</template>

<script>
import BananaButton from '@/components/ui/BananaButton.vue'

export default {
  name: 'RireDeTout',
  components: { BananaButton },
}
</script>

<style scoped>
/* ---- Hero ---- */
.rire__hero { text-align: center; padding: 56px 20px 24px; }
.rire__title { font-size: clamp(2.6rem, 8vw, 5rem); }
.hl { color: var(--banana-700); }
.rire__lead {
  font-family: var(--font-accent);
  font-style: italic;
  font-size: clamp(1.2rem, 2.8vw, 1.6rem);
  color: var(--ink-700);
  max-width: 600px;
  margin: 10px auto 0;
}

/* ---- Manifeste ---- */
.manifesto { display: flex; flex-direction: column; gap: 26px; padding: 20px 20px 10px; }
.manifesto__intro {
  font-family: var(--font-accent);
  font-size: clamp(1.1rem, 2.4vw, 1.35rem);
  line-height: 1.65;
  color: var(--ink-700);
  border-left: 5px solid var(--banana-500);
  padding-left: 20px;
  margin: 8px 0 6px;
}

.block { }
.block h2 { font-size: clamp(1.35rem, 3.4vw, 1.95rem); margin-bottom: 0.5em; }
.block p { font-size: 1.1rem; line-height: 1.75; color: var(--ink-800); margin: 0 0 0.9em; }
.block p:last-child { margin-bottom: 0; }
.block em { font-family: var(--font-accent); font-style: italic; }
.block strong { color: var(--ink-900); }

.tips { list-style: none; margin: 0.4em 0 1em; padding: 0; display: flex; flex-direction: column; gap: 12px; }
.tips li {
  position: relative;
  background: var(--surface);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-md);
  padding: 14px 18px 14px 44px;
  font-size: 1.05rem;
  line-height: 1.6;
  color: var(--ink-800);
  box-shadow: var(--shadow-sm);
}
.tips li::before {
  content: "🍌";
  position: absolute;
  left: 14px; top: 13px;
  font-size: 1.1rem;
}

/* ---- Creed (dark punchline card) ---- */
.creed {
  background: var(--choco);
  color: var(--banana-100);
  border-radius: var(--r-lg);
  border: var(--border-w) solid var(--border-c);
  box-shadow: var(--shadow-lg);
  padding: 34px 32px;
  text-align: center;
  margin: 10px 0 6px;
}
.creed__punch {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: clamp(1.4rem, 3.6vw, 2.1rem);
  color: var(--banana-400);
  margin: 0 0 0.5em;
  line-height: 1.1;
}
.creed__soft { font-size: 1.15rem; margin: 0 0 1em; opacity: 0.92; }
.creed__sign {
  font-family: var(--font-accent);
  font-style: italic;
  color: var(--banana-200);
  margin: 0;
  font-size: 1.05rem;
}

/* ---- Toolbox ---- */
.toolbox { display: flex; flex-direction: column; gap: 30px; padding: 40px 20px 20px; }
.toolbox__head { text-align: left; }
.toolbox__title { font-size: clamp(1.8rem, 5vw, 3rem); margin: 6px 0 0.5em; }
.toolbox__intro {
  font-family: var(--font-accent);
  font-style: italic;
  font-size: clamp(1.05rem, 2.3vw, 1.3rem);
  line-height: 1.65;
  color: var(--ink-700);
  margin: 0 0 20px;
}
.toolbox__reflex {
  background: var(--banana-100);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-md);
  padding: 18px 22px;
  box-shadow: var(--shadow-sm);
}
.toolbox__reflex-tag {
  display: inline-block;
  font-family: var(--font-display);
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.78rem;
  color: var(--banana-800);
  margin-bottom: 6px;
}
.toolbox__reflex p { margin: 0; font-size: 1.05rem; line-height: 1.65; color: var(--ink-800); }
.toolbox__reflex em { font-family: var(--font-accent); font-style: italic; }

/* ---- Tool card ---- */
.tool {
  display: flex;
  gap: 18px;
  background: var(--surface);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-lg);
  padding: 26px 28px;
  box-shadow: var(--shadow-md);
}
.tool__num {
  flex: none;
  width: 52px; height: 52px;
  display: grid;
  place-items: center;
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 1.5rem;
  color: var(--ink-900);
  background: var(--banana-500);
  border: var(--border-w) solid var(--border-c);
  border-radius: 50%;
  box-shadow: var(--shadow-pop);
}
.tool__body { min-width: 0; flex: 1; }
.tool__body h3 { font-size: clamp(1.2rem, 3vw, 1.55rem); margin-bottom: 0.5em; }
.tool__body > p { font-size: 1.06rem; line-height: 1.7; color: var(--ink-800); margin: 0 0 1em; }
.tool__body em { font-family: var(--font-accent); font-style: italic; }
.tool__body strong { color: var(--ink-900); }
.tool__note {
  font-size: 1rem !important;
  color: var(--ink-700) !important;
  margin-bottom: 0 !important;
}

/* ---- Avant → Après ---- */
.swap {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: stretch;
  gap: 12px;
  margin: 6px 0 14px;
}
.swap__before, .swap__after {
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-md);
  padding: 14px 16px;
}
.swap__before {
  background: rgba(26, 18, 7, 0.04);
  border-style: dashed;
}
.swap__before p {
  margin: 0;
  color: var(--ink-500);
  text-decoration: line-through;
  text-decoration-color: var(--bruise);
  text-decoration-thickness: 1.5px;
  font-size: 0.98rem;
  line-height: 1.55;
}
.swap__after {
  background: var(--banana-100);
  box-shadow: var(--shadow-sm);
}
.swap__after p {
  margin: 0;
  color: var(--ink-900);
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.55;
}
.swap__label {
  display: block;
  font-family: var(--font-display);
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 0.68rem;
  margin-bottom: 6px;
}
.swap__before .swap__label { color: var(--ink-300); }
.swap__after .swap__label { color: var(--banana-800); }
.swap__arrow {
  align-self: center;
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 1.6rem;
  color: var(--banana-700);
}

/* ---- Outro ---- */
.outro {
  border-left: 5px solid var(--banana-500);
  padding: 4px 0 4px 20px;
  margin-top: 8px;
}
.outro p {
  font-family: var(--font-accent);
  font-style: italic;
  font-size: 1.12rem;
  line-height: 1.7;
  color: var(--ink-700);
  margin: 0 0 0.9em;
}
.outro p:last-child { margin-bottom: 0; }

.rire__cta { text-align: center; padding: 26px 20px 60px; }

/* ---- Responsive ---- */
@media (max-width: 620px) {
  .rire__hero { padding: 40px 4px 20px; }
  .manifesto, .toolbox { padding-left: 4px; padding-right: 4px; }
  .swap { grid-template-columns: 1fr; gap: 8px; }
  .swap__arrow { transform: rotate(90deg); justify-self: center; font-size: 1.3rem; }
  .tool { flex-direction: column; gap: 14px; padding: 22px 20px; }
  .tool__num { width: 46px; height: 46px; font-size: 1.3rem; }
  .creed { padding: 28px 22px; }
}
</style>
